/**
 * The list below is the Mongo retention policy for biteLogs.
 * OLD defaults to 90 days in production, 2 days elsewhere. MAIN defaults to 2 days.
 * Rows can only lengthen each cluster's default.
 * Mongo-only readers call assertMongoCovers; archive-aware readers use planBiteLogRead.
 * Ranges are half-open: [fromMs, toMs). Archive reads still need availability checks.
 * This policy does not backfill rows that expired before deployment.
 */
import { BiteLogDeviceEvent, BiteLogKioskEvent, BiteLogMaitredEvent, BiteLogType, DatabaseClientKey, EnvironmentHelper, } from '@biteinc/enums';
import { DAY } from './time_helper.js';
export const LOYALTY_LOGIN_ERROR_PAIRS = [
    { type: BiteLogType.Maitred, status: BiteLogMaitredEvent.OrderLoyaltyAuthError },
    { type: BiteLogType.Maitred, status: BiteLogMaitredEvent.OrderLoyaltyGetAvailableRewardsError },
];
export const EXTENDED_MONGO_RETENTIONS = [
    {
        type: BiteLogType.Maitred,
        status: BiteLogMaitredEvent.OrderLoyaltyAuthError,
        retentionDays: 95,
        reason: 'Loyalty reports need 90 calendar days plus timezone/queue margin; BITE-20403 row 12.',
    },
    {
        type: BiteLogType.Maitred,
        status: BiteLogMaitredEvent.OrderLoyaltyGetAvailableRewardsError,
        retentionDays: 95,
        reason: 'Loyalty reports need 90 calendar days plus timezone/queue margin; BITE-20403 row 12.',
    },
    {
        type: BiteLogType.Maitred,
        status: BiteLogMaitredEvent.KorAlertSubscribed,
        retentionDays: 90,
        reason: 'Preserve the existing KOR subscription audit retention; BITE-20391.',
    },
    {
        type: BiteLogType.Maitred,
        status: BiteLogMaitredEvent.KorAlertUnsubscribed,
        retentionDays: 90,
        reason: 'Preserve the existing KOR subscription audit retention; BITE-20391.',
    },
    {
        type: BiteLogType.Kiosk,
        status: BiteLogKioskEvent.DeviceReboot,
        retentionDays: 30,
        reason: 'KOR dashboards and MCP show restarts for up to 7 days (~30 MB per 30 days); BITE-20390.',
    },
    {
        type: BiteLogType.Device,
        status: BiteLogDeviceEvent.ManualAppRestart,
        retentionDays: 30,
        reason: 'KOR dashboards and MCP show restarts for up to 7 days (~30 MB per 30 days); BITE-20390.',
    },
    {
        type: BiteLogType.Device,
        status: BiteLogDeviceEvent.KioskEnteredMaintenanceMode,
        retentionDays: 30,
        reason: 'KOR maintenance exit finds the matching entry (~1 GB per 30 days); BITE-20392.',
    },
    {
        type: BiteLogType.Device,
        status: BiteLogDeviceEvent.KioskUpdatedMaintenanceMode,
        retentionDays: 30,
        reason: 'KOR maintenance exit finds the matching entry (~1 GB per 30 days); BITE-20392.',
    },
    {
        type: BiteLogType.Device,
        status: BiteLogDeviceEvent.NativeTransactionStarted,
        retentionDays: 8,
        reason: 'The nightly transaction audit checks the last 7 days (~2.7 GB for both types); BITE-20558.',
    },
    {
        type: BiteLogType.Device,
        status: BiteLogDeviceEvent.NativeTransactionFinished,
        retentionDays: 8,
        reason: 'The nightly transaction audit checks the last 7 days (~2.7 GB for both types); BITE-20558.',
    },
];
function defaultMongoRetentionMs(isProd, databaseClientKey) {
    return (isProd && databaseClientKey === DatabaseClientKey.NON_CRITICAL ? 90 : 2) * DAY;
}
export function mongoRetentionMsFor(pair, isProd = EnvironmentHelper.isCurrentProd(), retentions = EXTENDED_MONGO_RETENTIONS, databaseClientKey = DatabaseClientKey.NON_CRITICAL) {
    const extendedRetention = retentions.find((entry) => entry.type === pair.type && entry.status === pair.status);
    return Math.max(defaultMongoRetentionMs(isProd, databaseClientKey), (extendedRetention?.retentionDays ?? 0) * DAY);
}
export function expireAtFor(pair, nowMs, isProd = EnvironmentHelper.isCurrentProd(), databaseClientKey = DatabaseClientKey.NON_CRITICAL) {
    return new Date(nowMs + mongoRetentionMsFor(pair, isProd, undefined, databaseClientKey));
}
export function planBiteLogRead({ pairs, fromMs, toMs, nowMs, isProd = EnvironmentHelper.isCurrentProd(), databaseClientKey, }) {
    if (fromMs > toMs || ![fromMs, toMs, nowMs].every(Number.isFinite)) {
        throw new RangeError('Bite log reads require finite timestamps and fromMs <= toMs.');
    }
    const retentionMs = Math.min(...pairs.map((pair) => mongoRetentionMsFor(pair, isProd, undefined, databaseClientKey)));
    const mongoFromMs = nowMs - retentionMs;
    const mongoStartMs = Math.max(fromMs, mongoFromMs);
    const archiveEndMs = Math.min(toMs, mongoFromMs);
    return {
        mongoFromMs,
        mongo: mongoStartMs < toMs ? { fromMs: mongoStartMs, toMs } : null,
        archive: fromMs < archiveEndMs ? { fromMs, toMs: archiveEndMs } : null,
    };
}
export function assertMongoCovers(options) {
    const plan = planBiteLogRead(options);
    if (plan.archive) {
        throw new Error(`${options.reader}: biteLogs Mongo coverage starts at ${new Date(plan.mongoFromMs).toISOString()}; ` +
            `the requested range starts at ${new Date(options.fromMs).toISOString()}.`);
    }
}
//# sourceMappingURL=bite_log_retention.js.map